import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; 
import LoadingSpinner from '../components/LoadingSpinner';
import { createNewResumeWithAI } from '../services/aiResumeCreateServiceNew';
import Modal from '../components/Modal';
import LoginModal from '../components/LoginModal';
import { GiStarsStack } from "react-icons/gi";

const CreateResume = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAILoading, setIsAILoading] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [aiErrorMessage, setAiErrorMessage] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Enhanced loading messages for better UX
  const EngagingTexts = [
    "🎯 Analyzing job requirements...",
    "📝 Crafting your professional summary...",
    "💼 Building work experience section...",
    "🎓 Adding education details...",
    "💡 Selecting relevant skills...",
    "🌟 Adding soft skills and languages...",
    "📍 Setting up location details...",
    "📸 Adding professional photo...",
    "✨ Polishing final details...",
    "🚀 Almost ready - finalizing your resume!"
  ];

  // Gender-specific name pools
  const maleFirstNames = [
    'James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph',
    'Thomas', 'Christopher', 'Charles', 'Daniel', 'Matthew', 'Anthony', 'Mark',
    'Donald', 'Steven', 'Paul', 'Andrew', 'Joshua', 'Kenneth', 'Kevin', 'Brian',
    'George', 'Timothy', 'Ronald', 'Jason', 'Edward', 'Jeffrey', 'Ryan', 'Jacob',
    'Gary', 'Nicholas', 'Eric', 'Jonathan', 'Stephen', 'Larry', 'Justin', 'Scott',
    'Brandon', 'Benjamin', 'Samuel', 'Gregory', 'Alexander', 'Patrick', 'Frank',
    'Raymond', 'Jack', 'Dennis', 'Jerry', 'Tyler', 'Aaron', 'Jose', 'Henry',
    'Adam', 'Douglas', 'Nathan', 'Peter', 'Zachary', 'Kyle', 'Noah', 'Alan',
    'Ethan', 'Jeremy', 'Lionel', 'Carl', 'Wayne', 'Ralph', 'Roy', 'Eugene',
    'Louis', 'Philip', 'Bobby'
  ];

  const femaleFirstNames = [
    'Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan',
    'Jessica', 'Sarah', 'Karen', 'Nancy', 'Lisa', 'Betty', 'Helen', 'Sandra',
    'Donna', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Laura', 'Sarah', 'Kimberly',
    'Deborah', 'Dorothy', 'Lisa', 'Nancy', 'Karen', 'Betty', 'Helen', 'Sandra',
    'Donna', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Laura', 'Sarah', 'Kimberly',
    'Amy', 'Angela', 'Ashley', 'Brenda', 'Emma', 'Olivia', 'Cynthia', 'Marie',
    'Janet', 'Catherine', 'Frances', 'Christine', 'Samantha', 'Debra', 'Rachel',
    'Carolyn', 'Janet', 'Virginia', 'Maria', 'Heather', 'Diane', 'Julie', 'Joyce',
    'Victoria', 'Kelly', 'Christina', 'Joan', 'Evelyn', 'Lauren', 'Judith',
    'Megan', 'Cheryl', 'Andrea', 'Hannah', 'Jacqueline', 'Martha', 'Gloria',
    'Teresa', 'Sara', 'Janice', 'Marie', 'Julia', 'Kathryn', 'Catherine', 'Frances',
    'Christine', 'Samantha', 'Debra', 'Rachel', 'Carolyn', 'Janet'
  ];

  const lastNames = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
    'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzales', 'Wilson', 'Anderson',
    'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
    'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker',
    'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill',
    'Flores', 'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell',
    'Mitchell', 'Carter', 'Roberts', 'Gomez', 'Phillips', 'Evans', 'Turner',
    'Diaz', 'Parker', 'Cruz', 'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris',
    'Morales', 'Murphy', 'Cook', 'Rogers', 'Gutierrez', 'Ortiz', 'Morgan',
    'Cooper', 'Peterson', 'Bailey', 'Reed', 'Kelly', 'Howard', 'Ramos', 'Kim',
    'Cox', 'Ward', 'Richardson', 'Watson', 'Brooks', 'Chavez', 'Wood', 'James',
    'Bennett', 'Gray', 'Mendoza', 'Ruiz', 'Hughes', 'Price', 'Alvarez', 'Castillo',
    'Sanders', 'Patel', 'Myers', 'Long', 'Ross', 'Foster', 'Jimenez'
  ];

  // Address data for different countries
  const addressData = {
    US: {
      cities: [
        { city: 'New York', state: 'NY', zipCodes: ['10001', '10002', '10003', '10004', '10005'] },
        { city: 'Los Angeles', state: 'CA', zipCodes: ['90001', '90002', '90003', '90004', '90005'] },
        { city: 'Chicago', state: 'IL', zipCodes: ['60601', '60602', '60603', '60604', '60605'] },
        { city: 'Houston', state: 'TX', zipCodes: ['77001', '77002', '77003', '77004', '77005'] },
        { city: 'Phoenix', state: 'AZ', zipCodes: ['85001', '85002', '85003', '85004', '85005'] },
        { city: 'Philadelphia', state: 'PA', zipCodes: ['19101', '19102', '19103', '19104', '19105'] },
        { city: 'San Antonio', state: 'TX', zipCodes: ['78201', '78202', '78203', '78204', '78205'] },
        { city: 'San Diego', state: 'CA', zipCodes: ['92101', '92102', '92103', '92104', '92105'] },
        { city: 'Dallas', state: 'TX', zipCodes: ['75201', '75202', '75203', '75204', '75205'] },
        { city: 'Austin', state: 'TX', zipCodes: ['73301', '73302', '73303', '73304', '73305'] }
      ],
      phoneFormats: [
        () => {
          const area = Math.floor(Math.random() * 800) + 200;
          const exchange = Math.floor(Math.random() * 800) + 200;
          const number = Math.floor(Math.random() * 9000) + 1000;
          return `(${area}) ${exchange}-${number}`;
        },
        () => {
          const area = Math.floor(Math.random() * 800) + 200;
          const exchange = Math.floor(Math.random() * 800) + 200;
          const number = Math.floor(Math.random() * 9000) + 1000;
          return `${area}-${exchange}-${number}`;
        },
        () => {
          const area = Math.floor(Math.random() * 800) + 200;
          const exchange = Math.floor(Math.random() * 800) + 200;
          const number = Math.floor(Math.random() * 9000) + 1000;
          return `${area}.${exchange}.${number}`;
        }
      ]
    },
    UK: {
      cities: [
        { city: 'London', state: 'England', zipCodes: ['SW1A 1AA', 'W1A 0AX', 'M1 1AA', 'B33 8TH', 'E1 6AN'] },
        { city: 'Manchester', state: 'England', zipCodes: ['M1 1AA', 'M2 2BB', 'M3 3CC', 'M4 4DD', 'M5 5EE'] },
        { city: 'Birmingham', state: 'England', zipCodes: ['B1 1AA', 'B2 2BB', 'B3 3CC', 'B4 4DD', 'B5 5EE'] },
        { city: 'Liverpool', state: 'England', zipCodes: ['L1 1AA', 'L2 2BB', 'L3 3CC', 'L4 4DD', 'L5 5EE'] },
        { city: 'Leeds', state: 'England', zipCodes: ['LS1 1AA', 'LS2 2BB', 'LS3 3CC', 'LS4 4DD', 'LS5 5EE'] },
        { city: 'Glasgow', state: 'Scotland', zipCodes: ['G1 1AA', 'G2 2BB', 'G3 3CC', 'G4 4DD', 'G5 5EE'] },
        { city: 'Edinburgh', state: 'Scotland', zipCodes: ['EH1 1AA', 'EH2 2BB', 'EH3 3CC', 'EH4 4DD', 'EH5 5EE'] },
        { city: 'Cardiff', state: 'Wales', zipCodes: ['CF1 1AA', 'CF2 2BB', 'CF3 3CC', 'CF4 4DD', 'CF5 5EE'] },
        { city: 'Belfast', state: 'Northern Ireland', zipCodes: ['BT1 1AA', 'BT2 2BB', 'BT3 3CC', 'BT4 4DD', 'BT5 5EE'] },
        { city: 'Bristol', state: 'England', zipCodes: ['BS1 1AA', 'BS2 2BB', 'BS3 3CC', 'BS4 4DD', 'BS5 5EE'] }
      ],
      phoneFormats: [
        () => {
          const area = Math.floor(Math.random() * 900) + 100;
          const number = Math.floor(Math.random() * 9000000) + 1000000;
          return `+44 ${area} ${number.toString().substring(0,3)} ${number.toString().substring(3)}`;
        },
        () => {
          const area = Math.floor(Math.random() * 900) + 100;
          const number = Math.floor(Math.random() * 9000000) + 1000000;
          return `0${area} ${number.toString().substring(0,3)} ${number.toString().substring(3)}`;
        }
      ]
    },
    IN: {
      cities: [
        { city: 'Mumbai', state: 'Maharashtra', zipCodes: ['400001', '400002', '400003', '400004', '400005'] },
        { city: 'Delhi', state: 'Delhi', zipCodes: ['110001', '110002', '110003', '110004', '110005'] },
        { city: 'Bangalore', state: 'Karnataka', zipCodes: ['560001', '560002', '560003', '560004', '560005'] },
        { city: 'Hyderabad', state: 'Telangana', zipCodes: ['500001', '500002', '500003', '500004', '500005'] },
        { city: 'Chennai', state: 'Tamil Nadu', zipCodes: ['600001', '600002', '600003', '600004', '600005'] },
        { city: 'Kolkata', state: 'West Bengal', zipCodes: ['700001', '700002', '700003', '700004', '700005'] },
        { city: 'Pune', state: 'Maharashtra', zipCodes: ['411001', '411002', '411003', '411004', '411005'] },
        { city: 'Ahmedabad', state: 'Gujarat', zipCodes: ['380001', '380002', '380003', '380004', '380005'] },
        { city: 'Jaipur', state: 'Rajasthan', zipCodes: ['302001', '302002', '302003', '302004', '302005'] },
        { city: 'Surat', state: 'Gujarat', zipCodes: ['395001', '395002', '395003', '395004', '395005'] }
      ],
      phoneFormats: [
        () => {
          const area = Math.floor(Math.random() * 900) + 100;
          const number = Math.floor(Math.random() * 90000000) + 10000000;
          return `+91 ${area.toString().substring(0,2)} ${number.toString().substring(0,4)} ${number.toString().substring(4)}`;
        },
        () => {
          const number = Math.floor(Math.random() * 9000000000) + 1000000000;
          return `+91 ${number.toString().substring(0,5)} ${number.toString().substring(5)}`;
        },
        () => {
          const number = Math.floor(Math.random() * 9000000000) + 1000000000;
          return `${number.toString().substring(0,5)} ${number.toString().substring(5)}`;
        }
      ]
    }
  };

  // Smart experience calculation based on job title
  const getExperienceForJob = (jobTitle) => {
    const jobTitleLower = jobTitle.toLowerCase();
    
    // C-Level/Executive positions (12-20 years)
    if (jobTitleLower.includes('ceo') || jobTitleLower.includes('cto') || 
        jobTitleLower.includes('cfo') || jobTitleLower.includes('chief') ||
        jobTitleLower.includes('executive')) {
      return Math.floor(Math.random() * 9) + 12; // 12-20 years
    }
    
    // Senior level positions (8-15 years)
    if (jobTitleLower.includes('senior') || jobTitleLower.includes('lead') || 
        jobTitleLower.includes('principal') || jobTitleLower.includes('director')) {
      return Math.floor(Math.random() * 8) + 8; // 8-15 years
    }
    
    // Manager positions (5-12 years)
    if (jobTitleLower.includes('manager') || jobTitleLower.includes('supervisor') || 
        jobTitleLower.includes('head of') || jobTitleLower.includes('team lead')) {
      return Math.floor(Math.random() * 8) + 5; // 5-12 years
    }
    
    // Specialist/Analyst positions (3-8 years)
    if (jobTitleLower.includes('specialist') || jobTitleLower.includes('analyst') || 
        jobTitleLower.includes('coordinator') || jobTitleLower.includes('associate') ||
        jobTitleLower.includes('consultant')) {
      return Math.floor(Math.random() * 6) + 3; // 3-8 years
    }
    
    // Junior/Entry level (1-4 years)
    if (jobTitleLower.includes('junior') || jobTitleLower.includes('entry') || 
        jobTitleLower.includes('trainee') || jobTitleLower.includes('intern') ||
        jobTitleLower.includes('assistant')) {
      return Math.floor(Math.random() * 4) + 1; // 1-4 years
    }
    
    // Default range for other positions (4-10 years)
    return Math.floor(Math.random() * 7) + 4; // 4-10 years
  };

  // Enhanced job title formatting with better handling
  const formatJobTitle = (rawJobTitle) => {
    return rawJobTitle
      .split(/[-_%20\s]+/) // Split on various separators
      .filter(word => word.length > 0) // Remove empty strings
      .map(word => {
        // Handle common abbreviations
        const upperWord = word.toUpperCase();
        if (['IT', 'HR', 'AI', 'ML', 'UI', 'UX', 'CEO', 'CTO', 'CFO'].includes(upperWord)) {
          return upperWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ')
      .trim();
  };

  // Generate random profile data
  const generateRandomProfile = () => {
    // Randomly choose gender
    const isMale = Math.random() < 0.5;
    const firstName = isMale 
      ? maleFirstNames[Math.floor(Math.random() * maleFirstNames.length)]
      : femaleFirstNames[Math.floor(Math.random() * femaleFirstNames.length)];
    
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    
    // Randomly choose country
    const countries = ['US', 'UK', 'IN'];
    const selectedCountry = countries[Math.floor(Math.random() * countries.length)];
    const countryData = addressData[selectedCountry];
    
    // Select random city from chosen country
    const selectedCityData = countryData.cities[Math.floor(Math.random() * countryData.cities.length)];
    const selectedZipCode = selectedCityData.zipCodes[Math.floor(Math.random() * selectedCityData.zipCodes.length)];
    
    // Generate phone number based on country
    const phoneFormatter = countryData.phoneFormats[Math.floor(Math.random() * countryData.phoneFormats.length)];
    const phoneNumber = phoneFormatter();
    
    // Generate photo URL based on gender
    const photoNumber = Math.floor(Math.random() * 20) + 1;
    const photoUrl = isMale 
    ? `/images/profile-pic/male-${photoNumber}.jpg`
    : `/images/profile-pic/female-${photoNumber}.jpg`;
    
    return {
      firstName,
      lastName,
      phone: phoneNumber,
      city: selectedCityData.city,
      state: selectedCityData.state,
      country: selectedCountry === 'US' ? 'United States' : selectedCountry === 'UK' ? 'United Kingdom' : 'India',
      postalCode: selectedZipCode,
      photoUrl,
      isMale
    };
  };

  // Rotating text effect for loading messages
  useEffect(() => {
    if (isAILoading) {
      const intervalId = setInterval(() => {
        setCurrentTextIndex(prevIndex => (prevIndex + 1) % EngagingTexts.length);
      }, 2500);
      return () => clearInterval(intervalId);
    }
  }, [isAILoading, EngagingTexts.length]);

  // Main effect for handling URL parameters and auto-generation
  useEffect(() => {
    if (!authLoading) {
      const queryParams = new URLSearchParams(location.search);
      const jobParam = queryParams.get('job') || queryParams.get('jobtitle');
      
      if (jobParam) {
        console.log('🎯 Job parameter detected:', jobParam);
        
        if (user) {
          // User is logged in, proceed with resume creation
          setIsAILoading(true);
          
          const jobTitle = formatJobTitle(jobParam);
          const experienceYears = getExperienceForJob(jobTitle);
          const profileData = generateRandomProfile();
          
          const formDataWithJobTitle = {
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            jobTitle: jobTitle,
            email: 'info@maxresumes.com',
            phone: profileData.phone,
            city: profileData.city,
            state: profileData.state,
            country: profileData.country,
            postalCode: profileData.postalCode,
            photoUrl: profileData.photoUrl,
            yearsOfExperience: experienceYears,
          };

          console.log('📋 Generated form data:', formDataWithJobTitle);
          handleCreateNewResumeWithAI(formDataWithJobTitle);
        } else {
          // User not logged in, show login modal
          setIsLoginModalOpen(true);
        }
      } else {
        // No job parameter, redirect to dashboard or show error
        console.log('❌ No job parameter found in URL');
        setAiErrorMessage('No job title specified. Please provide a job parameter in the URL.');
      }
    }
  }, [location.search, user, authLoading]);

  const handleCreateNewResumeWithAI = async (formData) => {
    setAiErrorMessage('');
    
    if (!user) {
      setIsLoginModalOpen(true);
      setIsAILoading(false);
      return;
    }

    const formDataWithUserID = {
      ...formData,
      userID: user.uid,
    };

    try {
      console.log('🚀 Starting AI resume creation...');
      console.log('📊 Form data:', formDataWithUserID);
      
      await createNewResumeWithAI(formDataWithUserID, navigate);
      console.log('✅ Resume created successfully!');
    } catch (error) {
      console.error("❌ Failed to create new resume with AI:", error);
      setAiErrorMessage('Failed to create resume. Please try again.');
    } finally {
      setIsAILoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    // After login, the useEffect will trigger again and create the resume
  };

  const handleTryAgain = () => {
    const queryParams = new URLSearchParams(location.search);
    const jobParam = queryParams.get('job') || queryParams.get('jobtitle');
    
    if (jobParam && user) {
      setAiErrorMessage('');
      setIsAILoading(true);
      
      const jobTitle = formatJobTitle(jobParam);
      const experienceYears = getExperienceForJob(jobTitle);
      const profileData = generateRandomProfile();
      
      const formDataWithJobTitle = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        jobTitle: jobTitle,
        email: 'info@maxresumes.com',
        phone: profileData.phone,
        city: profileData.city,
        state: profileData.state,
        country: profileData.country,
        postalCode: profileData.postalCode,
        photoUrl: profileData.photoUrl,
        yearsOfExperience: experienceYears,
      };

      handleCreateNewResumeWithAI(formDataWithJobTitle);
    }
  };

  if (authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='main-dashboard min-h-screen bg-gray-50'>
      {/* AI Loading Modal */}
      <Modal 
        isOpen={isAILoading} 
        closeModal={() => {}} 
        title=""
        showCloseButton={false}
      >
        <div className="text-center h-[250px] flex flex-col justify-center items-center px-6">
          <div className="mb-6">
            <GiStarsStack className="text-7xl text-blue-500 animate-pulse mx-auto" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-3">
            Creating Your Perfect Resume
          </h3>
          <p className="text-sm text-blue-600 font-medium transition-opacity duration-1000 ease-in-out mb-4">
            {EngagingTexts[currentTextIndex]}
          </p>
          <div className="mt-4">
            <LoadingSpinner displayType="normal" className="loadingSpin" />
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Please wait while we generate your resume...
          </p>
        </div>
      </Modal>

      {/* Login Modal */}
      {isLoginModalOpen && (
        <LoginModal 
          isOpen={isLoginModalOpen} 
          closeModal={() => setIsLoginModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <GiStarsStack className="text-6xl text-blue-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              AI Resume Generator
            </h1>
            
            {aiErrorMessage ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-center text-red-600 mb-4">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">Error</span>
                </div>
                <p className="text-red-700 mb-4">{aiErrorMessage}</p>
                {user && (
                  <button 
                    onClick={handleTryAgain}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Try Again
                  </button>
                )}
              </div>
            ) : (
              <div className="text-gray-600 space-y-4">
                <p className="text-lg">
                  Automatically generate professional resumes using AI
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">How it works:</h3>
                  <div className="text-left text-sm text-blue-700 space-y-1">
                    <p>• Add <code className="bg-blue-100 px-1 rounded">?job=YourJobTitle</code> to the URL</p>
                    <p>• We'll generate realistic profile data (gender-based names, global addresses)</p>
                    <p>• AI creates tailored content for your role</p>
                    <p>• Professional photos are automatically added</p>
                    <p>• Resume opens in the editor for customization</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Example URLs:</h4>
                  <div className="text-xs text-gray-600 space-y-1 font-mono">
                    <p>/create-resume?job=Software Engineer</p>
                    <p>/create-resume?job=Marketing Manager</p>
                    <p>/create-resume?job=Data Analyst</p>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-700 mb-2">✨ New Features:</h4>
                  <div className="text-left text-sm text-green-600 space-y-1">
                    <p>• Gender-specific names and professional photos</p>
                    <p>• Multi-country addresses (US, UK, India)</p>
                    <p>• Region-appropriate phone number formats</p>
                    <p>• Smart experience calculation based on job level</p>
                  </div>
                </div>
              </div>
            )}
            
            {!user && !isLoginModalOpen && (
              <div className="mt-6">
                <button 
                  onClick={() => setIsLoginModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                >
                  Login to Create Resume
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateResume;